import { useCallback, useEffect, useState } from 'react';

/** The person's choice: follow the computer, or always light or dark. */
export type ThemeChoice = 'system' | 'light' | 'dark';

export const THEME_KEY = 'mogzo-theme';

export function readTheme(): ThemeChoice {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Sets data-theme on <html>; none means tokens.css follows the computer. */
export function applyTheme(choice: ThemeChoice) {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/**
 * For a <script> in index.html's <head>, so the page paints in the right theme before React
 * starts.
 */
export const THEME_SCRIPT = `try{var t=localStorage.getItem('${THEME_KEY}');if(t==='light'||t==='dark')document.documentElement.setAttribute('data-theme',t)}catch(e){}`;

/** The theme choice, kept in this browser and the same on every MOGZO site on this origin. */
export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);
  useEffect(() => applyTheme(choice), [choice]);
  const set = useCallback((next: ThemeChoice) => {
    try {
      if (next === 'system') localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, next);
    } catch {
      // Private windows may refuse; the choice still holds for this visit.
    }
    setChoice(next);
  }, []);
  return [choice, set];
}

export const THEME_NAMES: Record<ThemeChoice, string> = {
  system: 'Same as this computer',
  light: 'Light',
  dark: 'Dark',
};
